import React, { useState, useEffect } from 'react';
import { ShieldCheck, RefreshCw, X, FileText, AlertTriangle } from 'lucide-react';

// Check with vSure — a REAL VEVO check, shared by the Immigration board (staff) and the
// applicant card. `load()` resolves the prepare payload ({ env, consent, document, source,
// readError, passportId | scanPath }); `run({ document } | { checkId })` runs or resumes
// the check; `viewScan(prep)` resolves { success, base64, contentType } or { url } for the
// passport image.

function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d + 'T00:00:00').toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
}

// A REAL VEVO check through vSure. The details are pre-filled — passport number from
// the record, country / DOB / names read off the stored passport scan (or saved from the
// last successful check) — and the office confirms them against the scan before running.
export default function VsureCheckModal({ name, load, run: runCheck, viewScan, consentLabel = 'Consent given', onClose, onSaved, onError }) {
  const [prep, setPrep] = useState(null);
  const [doc, setDoc] = useState(null);
  const [running, setRunning] = useState(false);
  const [outcome, setOutcome] = useState(null);
  useEffect(() => {
    let live = true;
    load().then((d) => {
      if (!live) return;
      if (d && d.success) { setPrep(d); setDoc(d.document); }
      else { onError(d?.error || 'Could not prepare the check'); onClose(); }
    }).catch((e) => { if (live) { onError(e.message); onClose(); } });
    return () => { live = false; };
  }, []); // eslint-disable-line

  const set = (k) => (e) => setDoc((p) => ({ ...p, [k]: k === 'country' || k === 'identifier' ? e.target.value.toUpperCase() : e.target.value }));
  const live = prep && prep.env === 'live';
  const missing = !doc ? 'loading' : !doc.identifier ? 'the passport number' : !/^[A-Z]{3}$/.test(doc.country || '') ? 'the 3-letter issuing country'
    : !doc.familyName ? 'the family name' : !doc.dateOfBirth ? 'the date of birth' : '';

  const viewPassport = async () => {
    const w = window.open('', '_blank');
    try {
      const d = await viewScan(prep);
      if (d && d.url) { if (w) w.location.href = d.url; return; }   // an already-served image
      if (!d || !d.success) throw new Error(d?.error || 'Could not open the passport');
      const bytes = Uint8Array.from(atob(d.base64), (c) => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: d.contentType || 'application/pdf' }));
      if (w) w.location.href = url; setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { if (w) w.close(); onError(e.message); }
  };

  const run = async () => {
    setRunning(true); setOutcome(null);
    try {
      let d = await runCheck({ document: doc });
      for (let i = 0; d && d.success && d.pending && i < 8; i += 1) {
        setOutcome({ pending: true });
        await new Promise((r) => setTimeout(r, 4000));
        d = await runCheck({ checkId: d.checkId });
      }
      if (!d || !d.success) { setOutcome({ error: d?.error || 'The check failed' }); return; }
      if (d.pending) { setOutcome({ error: 'vSure is still working on it. Close this and open it again in a few minutes — it will pick up the same check, not pay for a new one.' }); return; }
      if (d.saved) { await onSaved(); return; }
      setOutcome({ error: `${d.resultCode}: ${d.resultText}`, retryable: d.retryable });
    } catch (e) { setOutcome({ error: e.message }); }
    finally { setRunning(false); }
  };

  return (
    <div role="dialog" aria-modal="true" onClick={running ? undefined : onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: 'var(--hp-bg, #fff)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', width: 'min(560px, 100%)', maxHeight: '92vh', overflowY: 'auto', padding: '20px 22px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <ShieldCheck size={18} color="var(--hp-green)" />
          <h2 style={{ margin: 0, fontSize: '1.1rem', color: 'var(--hp-text)' }}>Check with vSure — {name}</h2>
          <button onClick={onClose} disabled={running} style={{ ...linkBtn, marginLeft: 'auto' }} aria-label="Close"><X size={16} /></button>
        </div>
        {!prep ? (
          <div style={{ color: 'var(--hp-dim)', fontSize: '0.84rem', padding: '18px 0' }}><RefreshCw size={14} className="spin" /> Reading the passport on file…</div>
        ) : (
          <>
            <p style={{ margin: '0 0 12px', fontSize: '0.78rem', color: 'var(--hp-dim)' }}>
              VEVO matches on passport number, issuing country and date of birth, so check these against the passport before you run it.
              {prep.source === 'scan' && ' They were read automatically from the passport scan.'}
              {prep.source === 'saved' && ' These matched on the last successful check.'}
              {prep.readError && ` The scan couldn't be read (${prep.readError}), so type them in.`}
              {' '}{consentLabel} {fmtDate((prep.consent.at || '').slice(0, 10))}.
            </p>
            {prep.passportKind === 'visa_passport' && (
              <div style={{ fontSize: '0.78rem', color: 'var(--hp-text)', background: 'rgba(59,130,246,0.10)', borderRadius: 'var(--radius)', padding: '8px 10px', marginBottom: 12 }}>
                Using the <strong>older passport the visa is on</strong>, not the current one — VEVO only knows the visa by that passport. Remove it from Compliance once Home Affairs has the new passport.
              </div>
            )}
            {!live && (
              <div style={{ fontSize: '0.78rem', color: '#b8860b', background: 'rgba(201,168,76,0.14)', borderRadius: 'var(--radius)', padding: '8px 10px', marginBottom: 12 }}>
                vSure is in <strong>sandbox</strong> mode. Real checks run once <code>VSURE_ENV</code> is set to <code>live</code> in Netlify.
              </div>
            )}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <Row label="Passport number" grow><input value={doc.identifier} onChange={set('identifier')} style={inp} /></Row>
              <Row label="Issuing country (3 letters)" grow><input value={doc.country} onChange={set('country')} maxLength={3} placeholder="e.g. COL" style={inp} /></Row>
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <Row label="Family name" grow><input value={doc.familyName} onChange={set('familyName')} style={inp} /></Row>
              <Row label="Given names" grow><input value={doc.givenName} onChange={set('givenName')} style={inp} /></Row>
            </div>
            <Row label="Date of birth"><input type="date" value={doc.dateOfBirth} onChange={set('dateOfBirth')} style={inp} /></Row>
            {viewScan && (prep.passportId || prep.scanPath) && <button onClick={viewPassport} style={{ ...ghostBtn, fontSize: '0.74rem' }}><FileText size={13} /> View passport scan</button>}

            {outcome && outcome.pending && <div style={{ marginTop: 12, fontSize: '0.8rem', color: 'var(--hp-dim)' }}><RefreshCw size={13} className="spin" /> vSure is checking with Home Affairs…</div>}
            {outcome && outcome.error && <div style={{ marginTop: 12, fontSize: '0.8rem', color: 'var(--hp-red)' }}><AlertTriangle size={13} /> {outcome.error}</div>}

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.72rem', color: 'var(--hp-dim)' }}>{missing && missing !== 'loading' ? `Still needed: ${missing}` : 'A successful check costs $2 (fast check).'}</span>
              <button onClick={onClose} disabled={running} style={{ ...ghostBtn, marginLeft: 'auto' }}>Cancel</button>
              <button onClick={run} disabled={!!missing || running || !live}
                style={{ ...ghostBtn, background: (missing || !live) ? 'var(--hp-surface)' : 'var(--hp-green)', color: (missing || !live) ? 'var(--hp-dim)' : '#fff', borderColor: (missing || !live) ? 'var(--hp-border)' : 'var(--hp-green)', cursor: (missing || !live) ? 'not-allowed' : 'pointer' }}>
                {running ? 'Checking…' : 'Run VEVO check'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Row({ label, grow, children }) {
  return (
    <div style={{ marginBottom: 12, flex: grow ? '1 1 200px' : undefined }}>
      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--hp-dim)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      {children}
    </div>
  );
}
const inp = { padding: '7px 9px', fontSize: '0.84rem', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', background: 'var(--hp-surface)', color: 'var(--hp-text)', width: '100%', boxSizing: 'border-box' };
const linkBtn = { display: 'inline-flex', alignItems: 'center', gap: 4, padding: 0, fontSize: '0.72rem', border: 'none', background: 'none', cursor: 'pointer', color: 'var(--hp-dim)' };
const ghostBtn = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', fontSize: '0.8rem', fontWeight: 700, border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', background: 'var(--hp-surface)', color: 'var(--hp-text)', cursor: 'pointer' };
